import { IconArrowLeft } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { type ColumnDef, DataTable } from '~/components/composites/data-table'
import { useQuery } from '~/hooks/use-query'
import type { ReferralDetailResponse, ReferredUser } from '~/types/referral'
import { QueryKey } from '~/types/query-key'

export function ReferralDetailPage() {
  const { t } = useTranslation()
  const { referrerId } = useParams<{ referrerId: string }>()

  const { data, isLoading } = useQuery<ReferralDetailResponse>({
    queryKey: QueryKey.ADMIN_REFERRAL_DETAIL,
    idParams: referrerId ? { id: referrerId } : undefined,
    options: { enabled: !!referrerId },
  })

  const referrer = data?.referrer
  const referredUsers = data?.referredUsers ?? []

  const columns: ColumnDef<ReferredUser>[] = [
    {
      key: 'username',
      header: t('referrals.detail.username'),
      render: (row) => <span className="font-medium">{row.username}</span>,
    },
    {
      key: 'name',
      header: t('referrals.detail.name'),
      render: (row) => row.name ?? '-',
    },
    {
      key: 'heroId',
      header: t('referrals.heroId'),
      render: (row) => row.heroId ?? '-',
      className: 'w-[200px]',
    },
    {
      key: 'referredAt',
      header: t('referrals.detail.joinedAt'),
      render: (row) => new Date(row.referredAt).toLocaleDateString(),
      className: 'w-[160px]',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link
          to="/referrals"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <IconArrowLeft size={16} />
          {t('referrals.detail.back')}
        </Link>
        <h1 className="text-2xl font-bold">
          {referrer
            ? t('referrals.detail.title', { username: referrer.username })
            : t('referrals.detail.titleFallback')}
        </h1>
        {referrer ? (
          <p className="text-sm text-muted-foreground">
            {t('referrals.heroId')}: {referrer.heroId ?? '-'} · {t('referrals.count')}: {referredUsers.length}
          </p>
        ) : null}
      </div>
      <DataTable
        columns={columns}
        data={referredUsers}
        loading={isLoading}
        emptyMessage={t('referrals.detail.empty')}
      />
    </div>
  )
}
